// Geo-Helpers: Distanz und Douglas-Peucker-Simplifikation.
// Wird sowohl client-seitig (Rendering) als auch server-seitig (Storage) genutzt.
const EARTH_R = 6371000; // Meter
export function toRad(deg) {
    return (deg * Math.PI) / 180;
}
// Haversine-Distanz in Metern.
export function haversine(aLat, aLon, bLat, bLon) {
    const phi1 = toRad(aLat);
    const phi2 = toRad(bLat);
    const dphi = toRad(bLat - aLat);
    const dl = toRad(bLon - aLon);
    const h = Math.sin(dphi / 2) ** 2 +
        Math.cos(phi1) * Math.cos(phi2) * Math.sin(dl / 2) ** 2;
    return 2 * EARTH_R * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
}
// Track-Länge in Metern
export function trackLength(points) {
    let total = 0;
    for (let i = 1; i < points.length; i++) {
        // Die Loop-Bedingung garantiert dass beide Indizes in-bounds sind;
        // tsc's noUncheckedIndexedAccess prüft das nicht → `!` ist sicher.
        const prev = points[i - 1];
        const curr = points[i];
        total += haversine(prev.lat, prev.lon, curr.lat, curr.lon);
    }
    return total;
}
// Höhenmeter (nur Anstiege/Abstiege, grob)
//
// Wave 14bk — Deadband gegen GPS-Höhenrauschen.
//
// Roh-GPX-Tracks von Bike-Computern / Smartwatches haben ±1-3m Noise auf
// der ele-Achse, auch bei physisch flachem Gelände. Ohne Deadband summiert
// sich das auf: ein 2.77km "flat" Trail kann dann mit 80m↑ / 80m↓ angezeigt
// werden obwohl er de facto zwei Höhenlinien durchquert. Das widerspricht
// dem User-Intent (z.B. bei Dondella, den der User aufgezeichnet hat und
// der in seiner Bike-App mit "↑0m / ↓449m" angezeigt wird).
//
// Strategie: Hysterese. Wir halten `lastCounted` als zuletzt als
// "signifikant" geltende ele fest. Kleine Deltas werden ignoriert UND
// aktualisieren lastCounted NICHT — erst wenn die kumulierte Differenz die
// Deadband-Schwelle überschreitet, zählen wir und setzen lastCounted auf
// die neue Höhe. Das ist korrekt für beides: echte graduelle Anstiege
// werden gezählt (weil lastCounted stehen bleibt bis ein signifikanter
// Zuwachs da ist) UND GPS-Noise um einen Mittelwert wird rausgefiltert
// (weil die Oszillationen unter der Schwelle bleiben).
//
// Default 2m: typisches GPS-Noise liegt bei ±1m, also 2m Peak-to-Peak.
// Für echte Trails ist 2m nicht einschränkend (auch der flachste Küsten-
// weg hat pro 100m mehr als 2m Höhenunterschied).
export function elevationStats(points, opts) {
    const dead = opts?.deadbandMeters ?? 0;
    let gain = 0;
    let loss = 0;
    let lastCounted = null;
    for (const p of points) {
        const e = p.ele;
        if (e == null || !Number.isFinite(e))
            continue;
        if (lastCounted == null) {
            lastCounted = e;
            continue;
        }
        const d = e - lastCounted;
        if (Math.abs(d) < dead)
            continue; // unter Schwelle → ignorieren, lastCounted bleibt
        if (d > 0)
            gain += d;
        else
            loss += -d;
        lastCounted = e;
    }
    return { gain, loss };
}
// Perpendicular Distance für Douglas-Peucker (in Metern).
// Approximation via Equirectangular-Projektion ist für Tracks < 50km genau genug.
function perpendicularDistance(p, a, b) {
    // lokale Meter-Projektion
    const lat0 = toRad((a.lat + b.lat) / 2);
    const mPerDegLat = 111320;
    const mPerDegLon = 111320 * Math.cos(lat0);
    const ax = a.lon * mPerDegLon;
    const ay = a.lat * mPerDegLat;
    const bx = b.lon * mPerDegLon;
    const by = b.lat * mPerDegLat;
    const px = p.lon * mPerDegLon;
    const py = p.lat * mPerDegLat;
    const dx = bx - ax;
    const dy = by - ay;
    const lenSq = dx * dx + dy * dy;
    if (lenSq === 0) {
        return Math.hypot(px - ax, py - ay);
    }
    // Parameter entlang A->B
    const t = ((px - ax) * dx + (py - ay) * dy) / lenSq;
    const tc = Math.max(0, Math.min(1, t));
    const cx = ax + tc * dx;
    const cy = ay + tc * dy;
    return Math.hypot(px - cx, py - cy);
}
/**
 * Douglas-Peucker Simplifikation für GPS-Tracks.
 * epsilonMeters: Abstand unter dem ein Punkt verworfen wird (z.B. 3-5m für MTB-Trails).
 * Zielgröße für offline: Trail-Tracks sollten nach Simplify meist < 500 Punkte haben.
 */
export function simplifyTrack(points, epsilonMeters = 4) {
    if (points.length < 3)
        return points.slice();
    const keep = new Array(points.length).fill(false);
    keep[0] = true;
    keep[points.length - 1] = true;
    const stack = [[0, points.length - 1]];
    while (stack.length) {
        const [s, e] = stack.pop();
        let maxDist = 0;
        let idx = -1;
        for (let i = s + 1; i < e; i++) {
            // s, e und i sind durch den Stack + die Loop garantiert in-bounds.
            const d = perpendicularDistance(points[i], points[s], points[e]);
            if (d > maxDist) {
                maxDist = d;
                idx = i;
            }
        }
        if (idx !== -1 && maxDist > epsilonMeters) {
            keep[idx] = true;
            stack.push([s, idx]);
            stack.push([idx, e]);
        }
    }
    const out = [];
    for (let i = 0; i < points.length; i++)
        if (keep[i])
            out.push(points[i]);
    return out;
}
// Ligurien-Bounds für Kamera-Initialisierung der Karte
export const LIGURIA_BOUNDS = {
    west: 7.45,
    south: 43.75,
    east: 9.95,
    north: 44.55,
};
export const LIGURIA_CENTER = [8.2, 44.15];
// Wave 14cf — Elba (Tyrrhenische Insel vor der Toskana).
// Zweite "richtige" Region neben Liguria. Eigenes PMTiles-Paket
// (elba.pmtiles + _contours/_hillshade) damit Offline-Speicher pro Region
// verwaltet werden kann.
export const ELBA_BOUNDS = {
    west: 10.10,
    south: 42.69,
    east: 10.46,
    north: 42.86,
};
export const ELBA_CENTER = [10.27, 42.78];
// Wave 14do — Finale Outdoor Region. Kleiner Sub-Ausschnitt innerhalb
// Liguria, ausgerichtet auf das berühmte MTB/Outdoor-Gebiet um Finale
// Ligure (von Loano im Westen bis Spotorno im Osten, nördlich bis Calice
// Ligure / Mallare). BBOX bewusst klein gehalten damit die PMTiles + die
// navi-DB-Datei deutlich unter Liguria-Größe bleiben (User-Wunsch:
// 'die ganzen liguria daten mit über 600mb sollten optional sein, aber
// neben liguria und elba sollte auch FINALE OUTDOOR REGION als optin
// stehen, dann die daten von dem gebiet anbieten').
// Wave 14dp: West-Grenze von 8.10 → 7.95 erweitert (Erli/Onzo).
// Wave 14dq: West-Grenze nochmal auf 7.80 — Trails bei Cerisola fielen
// in 14dp noch raus. Center entsprechend nach Westen verschoben.
export const FINALE_OUTDOOR_BOUNDS = {
    west: 7.80,
    south: 44.05,
    east: 8.45,
    north: 44.30,
};
export const FINALE_OUTDOOR_CENTER = [8.13, 44.17];
// Wave 14ek — Maritime Alps (S. geo.ts fuer Details).
export const MARITIME_ALPS_BOUNDS = {
    west: 5.50,
    south: 43.40,
    east: 7.55,
    north: 44.45,
};
export const MARITIME_ALPS_CENTER = [6.52, 43.92];
// Wave 14es — Pyrenees Coast (Andorra → Cote Vermeille). S. geo.ts fuer Details.
export const PYRENEES_COAST_BOUNDS = {
    west: 1.35,
    south: 42.25,
    east: 3.15,
    north: 42.95,
};
export const PYRENEES_COAST_CENTER = [2.25, 42.60];
// Wave 14es-fix5 — Pyrenees Coast (FR). Siehe geo.ts.
export const PYRENEES_COAST_FR_BOUNDS = {
    west: 2.50,
    south: 42.25,
    east: 3.15,
    north: 42.95,
};
export const PYRENEES_COAST_FR_CENTER = [2.85, 42.55];
// ULTRA — Korridor um die 3-Peaks-Bike-Race-Route (Wien → Barcelona).
// Einzige Region der ULTRA-App. Mirror von geo.ts CORRIDOR_BOUNDS/CENTER.
export const CORRIDOR_BOUNDS = {
    west: 1.59,
    south: 41.05,
    east: 16.78,
    north: 48.55,
};
export const CORRIDOR_CENTER = [9.1, 45.3];
// Wave 14ed — KEINE fest einkompilierte Region mehr.
//
// User (2026-08-04): "wenn ich die app zum ersten mal installiere sollen gar
// keine offline komponenten da sein und ich baue mir dann i-wann meinen
// korridor, kein zwang am anfang, keine 3 peaks route, nix!" — und: ein
// eingeladener Freund darf "3 Peaks Route" NICHT als unloeschbare aktive
// Region im Profil sehen.
//
// Deshalb ist die Registry jetzt LEER. Regionen entstehen ausschliesslich
// dynamisch: der Server listet unter /api/tiles/manifest genau die Korridore,
// die DIESEM User gehoeren (backend/routes/tiles.js -> corridorRegionEntry).
// Frischer Account = leeres Manifest = keine Region, kein Download, nichts.
//
// Folgen, die man kennen muss:
//   - REGION_IDS ist [] -> OfflinePage zeigt keine Platzhalter-Kacheln mehr.
//   - DEFAULT_REGION ist '' -> "noch keine Region gewaehlt". Jeder Consumer
//     muss den Leer-Fall abfangen (REGIONS[x]?.… reicht, ist ueberall so).
//   - isRegionId() ist damit immer false; der Unterschied "statisch vs.
//     dynamisch" existiert praktisch nicht mehr, die Guards bleiben aber
//     stehen, damit ein spaeterer statischer Eintrag wieder funktioniert.
//   - RegionId ist jetzt `string` (Korridor-Slugs sind zur Compile-Zeit
//     unbekannt) — die Laufzeit-Validierung macht isDynamicRegionSlug()
//     in apps/rider/src/lib/useUiSettings.ts bzw. der Server.
// CORRIDOR_BOUNDS/CORRIDOR_CENTER bleiben als reine Fallback-Konstanten
// erhalten (Kamera-Fallback), sind aber KEINE Region mehr.
export const REGIONS = {};
export const REGION_IDS = [];
/** '' = noch keine Region. Bewusst kein Fallback auf einen echten Korridor. */
export const DEFAULT_REGION = '';
export function isRegionId(x) {
    return typeof x === 'string' && Object.prototype.hasOwnProperty.call(REGIONS, x);
}
